import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useParams, useNavigate } from "react-router-dom";
import { 
  Shield, 
  Lock, 
  CheckCircle, 
  CreditCard, 
  ArrowLeft,
  Check,
  Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_luxury-credit-eval/artifacts/8w7azhpp_a8299614-dead-4387-811d-09deff6f81fe.png";

const CheckoutPage = () => {
  const { evaluationId } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cardDetails, setCardDetails] = useState({
    number: "",
    expiry: "",
    cvc: "",
    name: ""
  });

  const fetchEvaluation = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/evaluations/${evaluationId}`);
      setEvaluation(response.data);
    } catch (error) {
      console.error("Error fetching evaluation:", error);
      toast.error("Could not load order details");
      navigate("/evaluation");
    } finally {
      setLoading(false);
    }
  }, [evaluationId, navigate]);

  useEffect(() => {
    fetchEvaluation();
  }, [fetchEvaluation]);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(" ") : value;
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (v.length >= 2) {
      return v.substring(0, 2) + "/" + v.substring(2, 4);
    }
    return v;
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        evaluation_id: evaluationId,
        payment_method: paymentMethod
      });

      // Simulate payment processing (mock)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Complete order
      await axios.patch(`${API}/orders/${orderResponse.data.id}/complete`);

      setOrderComplete(true);
      toast.success("Payment successful! Your evaluation is being processed.");
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={48} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-[#10B981] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-white" />
          </div>
          <h1 className="font-serif text-3xl mb-4 text-white" data-testid="order-success-title">
            Payment <span className="gold-gradient-text">Successful!</span>
          </h1>
          <p className="text-[#A1A1AA] mb-8 font-sans">
            Thank you for your purchase! Our team will begin your credit evaluation 
            shortly. You will receive an email with your results within 24-48 hours.
          </p>
          <Link 
            to="/"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="return-home-btn"
          >
            Return to Home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/5 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="checkout-logo">
            <img src={LOGO_URL} alt="Line of Luxury" className="h-12" />
          </Link>
          <Link 
            to="/evaluation" 
            className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors flex items-center gap-2 text-sm"
            data-testid="checkout-back-link"
          >
            <ArrowLeft size={16} />
            Back to Evaluation
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title */}
            <div className="text-center mb-10">
              <h1 className="font-serif text-3xl md:text-4xl mb-4" data-testid="checkout-title">
                Secure <span className="gold-gradient-text">Checkout</span>
              </h1>
              <p className="text-[#A1A1AA] font-sans">
                Complete your payment to begin your credit evaluation
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Order Summary */}
              <div className="order-summary p-6" data-testid="order-summary">
                <h2 className="font-serif text-xl mb-6 text-white">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-start pb-4 border-b border-white/5">
                    <div>
                      <p className="text-white font-sans font-medium">Credit Evaluation Service</p>
                      <p className="text-[#A1A1AA] text-sm">Comprehensive credit analysis & action plan</p>
                    </div>
                    <p className="text-[#D4AF37] font-serif text-xl" data-testid="service-price">$15.00</p>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-white/5">
                  <p className="text-white font-sans font-medium">Total</p>
                  <p className="text-[#D4AF37] font-serif text-2xl" data-testid="total-price">$15.00</p>
                </div>

                {/* What's Included */}
                <div className="mt-8 pt-6 border-t border-white/5">
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-4">What's Included:</p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                      <CheckCircle size={16} className="text-[#D4AF37]" />
                      Comprehensive credit report review
                    </li>
                    <li className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                      <CheckCircle size={16} className="text-[#D4AF37]" />
                      Identification of disputable items
                    </li>
                    <li className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                      <CheckCircle size={16} className="text-[#D4AF37]" />
                      Personalized action plan
                    </li>
                    <li className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                      <CheckCircle size={16} className="text-[#D4AF37]" />
                      Expert consultation call
                    </li>
                  </ul>
                </div>

                {/* Customer Info */}
                {evaluation && (
                  <div className="mt-8 pt-6 border-t border-white/5">
                    <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-4">Customer:</p>
                    <p className="text-white font-sans" data-testid="customer-name">{evaluation.full_name}</p>
                    <p className="text-[#A1A1AA] text-sm" data-testid="customer-email">{evaluation.email}</p>
                  </div>
                )}
              </div>

              {/* Payment Form */}
              <div className="checkout-card p-6" data-testid="payment-form-card">
                <h2 className="font-serif text-xl mb-6 text-white">Payment Details</h2>

                {/* Payment Method Selection */}
                <div className="flex gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("card")}
                    className={`flex-1 p-4 border transition-colors flex items-center justify-center gap-2 ${
                      paymentMethod === "card" 
                        ? "border-[#D4AF37] bg-[#D4AF37]/10" 
                        : "border-white/10 hover:border-white/20"
                    }`}
                    data-testid="payment-method-card"
                  >
                    <CreditCard size={20} className={paymentMethod === "card" ? "text-[#D4AF37]" : "text-[#A1A1AA]"} />
                    <span className={paymentMethod === "card" ? "text-[#D4AF37]" : "text-[#A1A1AA]"}>Card</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("applepay")}
                    className={`flex-1 p-4 border transition-colors flex items-center justify-center gap-2 ${
                      paymentMethod === "applepay" 
                        ? "border-[#D4AF37] bg-[#D4AF37]/10" 
                        : "border-white/10 hover:border-white/20"
                    }`}
                    data-testid="payment-method-applepay"
                  >
                    <span className={`font-semibold ${paymentMethod === "applepay" ? "text-[#D4AF37]" : "text-[#A1A1AA]"}`}>
                       Pay
                    </span>
                  </button>
                </div>

                <form onSubmit={handlePayment}>
                  {paymentMethod === "card" && (
                    <div className="space-y-4">
                      {/* Card Number */}
                      <div>
                        <label className="block text-sm uppercase tracking-wider text-[#A1A1AA] mb-2">
                          Card Number
                        </label>
                        <input
                          type="text"
                          value={cardDetails.number}
                          onChange={(e) => setCardDetails({
                            ...cardDetails, 
                            number: formatCardNumber(e.target.value)
                          })}
                          className="luxury-input"
                          placeholder="1234 5678 9012 3456"
                          maxLength={19}
                          data-testid="card-number-input"
                        />
                      </div>

                      {/* Expiry & CVC */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm uppercase tracking-wider text-[#A1A1AA] mb-2">
                            Expiry Date
                          </label>
                          <input
                            type="text"
                            value={cardDetails.expiry}
                            onChange={(e) => setCardDetails({
                              ...cardDetails, 
                              expiry: formatExpiry(e.target.value)
                            })}
                            className="luxury-input"
                            placeholder="MM/YY"
                            maxLength={5}
                            data-testid="card-expiry-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm uppercase tracking-wider text-[#A1A1AA] mb-2">
                            CVC
                          </label>
                          <input
                            type="text"
                            value={cardDetails.cvc}
                            onChange={(e) => setCardDetails({
                              ...cardDetails, 
                              cvc: e.target.value.replace(/[^0-9]/g, "").substring(0, 4)
                            })}
                            className="luxury-input"
                            placeholder="123"
                            maxLength={4}
                            data-testid="card-cvc-input"
                          />
                        </div>
                      </div>

                      {/* Cardholder Name */}
                      <div>
                        <label className="block text-sm uppercase tracking-wider text-[#A1A1AA] mb-2">
                          Cardholder Name
                        </label>
                        <input
                          type="text"
                          value={cardDetails.name}
                          onChange={(e) => setCardDetails({...cardDetails, name: e.target.value})}
                          className="luxury-input"
                          placeholder="Name on card"
                          data-testid="card-name-input"
                        />
                      </div>
                    </div>
                  )}

                  {paymentMethod === "applepay" && (
                    <div className="bg-[#111111] p-8 text-center border border-white/5">
                      <p className="text-[#A1A1AA] font-sans">
                        Click "Complete Payment" to proceed with Apple Pay
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={processing}
                    className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
                    data-testid="complete-payment-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Lock size={18} />
                        Complete Payment - $15.00
                      </>
                    )}
                  </button>
                </form>

                {/* Trust Badges */}
                <div className="flex flex-wrap justify-center gap-4 mt-6 pt-6 border-t border-white/5">
                  <div className="trust-badge">
                    <Shield size={16} />
                    <span className="text-xs">Secure Checkout</span>
                  </div>
                  <div className="trust-badge">
                    <Lock size={16} />
                    <span className="text-xs">Encrypted</span>
                  </div>
                </div>

                {/* Money Back Note */}
                <p className="text-center text-[#52525B] text-xs mt-6 font-sans">
                  By completing this purchase, you agree that all sales are final.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#52525B] text-sm font-sans">
          © {new Date().getFullYear()} Line of Luxury LLC. All rights reserved.
        </p>
        </div>
      </footer>
    </div>
  );
};

export default CheckoutPage;
